import {ServiceBase} from '../../ServiceBase';
import {CmsBannerId} from '../cms-banners/cm-banner-id/cms-banner-id';
import {BannerData, CmsPageData, CmsSegmentsData, ICmsApiService} from './cms-api.service.interface';
import {LanguageSwitcherConfiguration} from '../../../app-bootstrap/config-interfaces/b2c-startup.configurations';
import {Check} from '@sparkware/app-utils';


export class CmsApiService extends ServiceBase implements ICmsApiService {

  private get _apiOrigin(): string {
    return this.services.launcher.getCMSOrigin() || this.services.location.currentUri.origin
  }

  private get _publicationID(): number {
    return this.services.configuration.cmsPublicationId;
  }

  async getBanners(bannersIds: CmsBannerId[]): Promise<BannerData[]> {
    try {
      if(!bannersIds?.length){
        return [];
      }

      let data = null;
      const options = {
        headers: this._getBannersRequestOptions()
      };

      if(this.services.configuration.servicesConfigs.Skin.isSiteInfraAWS) {
        data = await this.services.http.get(this._getBannersRequestUrl + this._getBannersRequestParamsGET(bannersIds), options);
      } else {
        options['body'] = this._getBannersRequestParamsPOST(bannersIds);
        data = await this.services.http.post(this._getBannersRequestUrl, options);
      }


      return data || [];
    } catch (err) {
      let bannersDescriptions = bannersIds.reduce((acc, curr) => acc + '\n' + curr.toString(), '');
      this.services.logger.error('Failed read the following banners from CMS API: ' + bannersDescriptions + 'error message: ' ,err);
      return [];
    }
  }

  async getFallbackBanners(fileNames: string[]): Promise<{[key: string]: string}> {
    try {
      return await this._readFallbackBanners(fileNames);
    } catch (err) {
      let files = fileNames.reduce((acc, curr) => acc + '\n' + curr, '');
      this.services.logger.error('Failed to read fallback banners for ' + files);
      return {};
    }
  }

  private _readFallbackBanners(fileNames: string[]): Promise<{[key: string]: string}> {


    let result = {};

    fileNames = fileNames || [];
    if(fileNames.length === 0) {
      return Promise.resolve(result);
    }


    let configuredFallbackUrl: string = this._getFallbackUrlFromConfig();
    if (!configuredFallbackUrl) {
      return Promise.resolve(result);
    }

    let promises: Promise<void>[] = [];
    for (let i = 0; i < fileNames.length; i++ ) {
      let fileName = fileNames[i];
      let computedFallbackUrl = configuredFallbackUrl.replace('{fileName}', fileName);
      let promise = this.services.http.get(computedFallbackUrl).then(content => {
        result[fileName] = content;

      }).catch(err => {
        this.services.logger.error(`Failed to load fallback CMS banner! fallbackUrl: ${computedFallbackUrl}`, err);
      });

      promises.push(promise);
    }

    return Promise.all(promises).then(() => result);
  }


  getPageContent(url: string): Promise<CmsPageData> {
    let options = {
      headers: this._getPagesRequestOptions()
    };

    if(this.services.miniOrbit.isEnabled) {
      url = url.replace(this.services.miniOrbit.currentMiniOrbitConfig.basePath, '/').replace('//', '/');
    }

    return this.services.http.get(this._apiOrigin + '/api/pagecontent?publicationid=' + this._publicationID + '&url=' + this._addSlashesInTheSides(url), options);
  }

  private _404PageContent: CmsPageData | null = null;
  async get404PageContent() {
    if(this._404PageContent) {
      return this._404PageContent;
    }

    try {
      let data = await this.services.http.get(this._apiOrigin + '/api/pagecontent?publicationid=' + this._publicationID + '&url=error404.aspx');
      this._404PageContent = {
        title: null,
        location: null,
        content: data && data.content
      };

      return this._404PageContent;
    } catch (err) {
      this.services.logger.error(`Failed to get 404 page content!`, err);
      return {
        title: null,
        location: null,
        content: '<div style="color:red; font-size: 25px; left:30px;margin-top: 100px;position:relative; z-index:8;line-height: 200px;">Something went wrong. Please try again.</div>'
      };
    }
  }

  private _getFallbackUrlFromConfig(): string {
    let configData = this.services.configuration.data;
    return configData && configData.generalSettings && configData.generalSettings.fallbackCMSComponentsPath;

  }


  private get _getBannersRequestUrl() {
    return this._apiOrigin + '/api/content/';
  }

  private _getBannersRequestParamsPOST(bannersIds: CmsBannerId[]): BodyInit {

    let params = this.services.http.createUrlSearchParams();
    params.append('publicationid', this._publicationID.toString());
    params.append('PagePlaceHoldersXML', '<placeholders><ReplacementPair><Key>PagePlaceholders_ValidKey</Key><Value>PagePlaceholders_ValidValue</Value></ReplacementPair></placeholders>');

    for(let i = 0; i < bannersIds.length; i++) {
      let bannerId = bannersIds[i];

      params.append(`components[${i}][templateId]`, bannerId.templateID);
      params.append(`components[${i}][componentId]`, bannerId.componentID);


    }

    return params;
  }

  private _getBannersRequestParamsGET(bannersIds: CmsBannerId[]): string {


    const publicationid = this._publicationID.toString();
    const PagePlaceHoldersXML = encodeURIComponent('<placeholders><ReplacementPair><Key>PagePlaceholders_ValidKey</Key><Value>PagePlaceholders_ValidValue</Value></ReplacementPair></placeholders>');

    let queryParams = `?publicationId=${publicationid}&PagePlaceHoldersXML=${PagePlaceHoldersXML}&`;

    let componentsParams = [];
    for(let i = 0; i < bannersIds.length; i++) {
      let bannerId = bannersIds[i];

      componentsParams.push(`templateId=${bannerId.templateID}`);
      componentsParams.push(`componentId=${bannerId.componentID}`);
    }

    return queryParams + componentsParams.join('&');
  }

  private _getBannersRequestOptions(): HeadersInit {

    let headers = this.services.http.createHeaders();
    headers.append('Content-Type', 'application/x-www-form-urlencoded; charset=UTF-8');
    return this.addXXContext(headers);
  }

  private _getPagesRequestOptions(): HeadersInit {

    let headers = this.services.http.createHeaders();
    return this.addXXContext(headers);
  }

  private _getSegmentesRequestOptions(): HeadersInit {
    let headers = this.services.http.createHeaders();
    return this.addXXContext(headers);
  }

  private addXXContext(headers:Headers):HeadersInit{
    let userSignature = this.services.sdk.getCmsUserSignature();
    if(!Check.isEmpty(userSignature)) {
      headers.append('XX.Context', userSignature && userSignature.message);
      headers.append('XX.Context.Signature', userSignature && userSignature.signature);
    }
    return headers;
  }

  async getSegments(): Promise<CmsSegmentsData> {
    try {
      let options = {
        headers: this._getSegmentesRequestOptions()
      };
      return await this.services.http.get(`${this._apiOrigin}/api/segments`, options);
    }catch (err) {
      this.services.logger.error(`Failed to read segments from CMS API!`, err);
      return {};
    }
  }

  async getLanguageSwitcherConfiguration(url: string): Promise<LanguageSwitcherConfiguration | null> {
    try {
      return await this.services.http.get(`${this._apiOrigin}/api/languageswitcher?publicationid=${this._publicationID}&url=${this._addSlashesInTheSides(url)}`);
    } catch (err) {
      this.services.logger.error(`Failed to get language switcher configuration for page ${url}`, err);
      return null;
    }
  }

  private _isRouteToHtmlFile(pathName: string): boolean {
    return pathName.endsWith('.htm')
          || pathName.endsWith('.html');
  }

  private _addSlashesInTheSides(pathName) {
    if(pathName && pathName[0] !== '/') {
      pathName = '/' + pathName;
    }

    //we append the trailing slash if the path doesn't ends with slash and also the path doesn't ends with .html or .htm
    //we don't append the trailing slash for paths like /path/file.htm or /path/file.html
    if(pathName && !this._isRouteToHtmlFile(pathName) && pathName[pathName.length - 1] !== '/') {
      pathName = pathName + '/';
    }

    if(pathName) {
      return pathName;
    }

    return '/';
  }

}
